<template>
    <Teleport to="body">
        <div
            v-bind="$attrs"
            :id="id"
            ref="modalEl"
            :data-bs-backdrop="staticBackdrop ? 'static' : 'true'"
            :data-bs-keyboard="keyboardDismiss ? 'true' : 'false'"
            tabindex="-1"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="labelledById"
            :aria-label="$attrs['aria-label'] || (hideHeader && !labelledById ? modalTitle : undefined)"
            :class="['modal fade', size, { show: isShown, 'modal-mobile': isMobile }]"
        >
            <div
                :class="[
                    'modal-dialog modal-dialog-centered',
                    dialogClass,
                    modalFullscreen ? 'modal-fullscreen' : '',
                    mobileLayoutClasses
                ]"
            >
                <div class="modal-content">
                    <div v-if="!hideHeader" class="modal-header">
                        <h2 :id="titleId" class="modal-title fs-5">
                            <span>{{ modalTitle }}</span>
                            <small v-if="modalSubtitle">{{ modalSubtitle }}</small>
                        </h2>
                        <button
                            type="button"
                            class="btn-close"
                            aria-label="Close"
                            :disabled="isBusy"
                            @click="requestClose"
                        />
                    </div>
                    <template v-if="$slots.body || $slots.footer">
                        <slot name="body" />
                        <slot name="footer" />
                    </template>
                    <slot v-else />
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script>
import { Modal } from "bootstrap";
import { dialogTrigger } from "@/composables/useDialogFocus";
import { registerModal, modalShown, releaseModal, topModal, modalBackdrop } from "./modalStack";
import { modalBusyOwner } from "@/composables/useModalBusy";

export default {
    provide() {
        return {
            [modalBusyOwner]: (token, busy) => {
                if (busy) this.busyChildren.add(token);
                else this.busyChildren.delete(token);
            }
        };
    },
    inheritAttrs: false,
    props: {
        modalTitle: { type: String, default: "" },
        modalSubtitle: { type: String, default: "" },
        id: { type: String, required: true },
        size: { type: String, default: "" },
        dialogClass: { type: String, default: "" },
        fullscreen: { type: Boolean, default: false },
        mobileFullscreen: { type: Boolean, default: true },
        mobileBodySpacing: {
            type: String,
            default: "normal",
            validator: (value) => ["normal", "compact"].includes(value)
        },
        bodySafeBottom: { type: Boolean, default: false },
        hideHeader: { type: Boolean, default: false },
        staticBackdrop: { type: Boolean, default: true },
        keyboardDismiss: { type: Boolean, default: false },
        busy: { type: Boolean, default: false },
        beforeClose: { type: Function, default: null }
    },
    // `shown` fires once the Bootstrap transition has finished and the body is
    // actually visible. Content that needs layout or a live element (a camera
    // feed, a focused input, a chart) should start on this, not on open().
    emits: ["close", "shown"],
    expose: ["close", "open"],
    data() {
        return {
            isShown: false,
            openedModal: null,
            suppressNextHidden: false,
            showHandler: null,
            returnFocusTarget: null,
            shownHandler: null,
            hiddenHandler: null,
            hideHandler: null,
            keydownHandler: null,
            dismissalPending: false,
            approvedDismissal: false,
            phase: "hidden",
            destroying: false,
            closePromise: null,
            resolveClose: null,
            closeWhenShown: false,
            userCloseWhenShown: false,
            busyChildren: new Set()
        };
    },
    computed: {
        isBusy() { return this.busy || this.busyChildren.size > 0; },
        modalFullscreen() {
            if (this.fullscreen) return true;
            return this.isMobile && this.mobileFullscreen;
        },
        isMobile() {
            return this.$store.state.isMobile;
        },
        mobileLayoutClasses() {
            if (!this.isMobile || !this.modalFullscreen) return [];

            return [
                `modal-mobile-body-${this.mobileBodySpacing}`,
                this.hideHeader ? "modal-mobile-header-hidden" : "",
                this.bodySafeBottom ? "modal-mobile-body-safe-bottom" : ""
            ];
        },
        titleId() {
            return `${this.id}-title`;
        },
        labelledById() {
            if (this.$attrs['aria-labelledby']) return this.$attrs['aria-labelledby'];
            return this.hideHeader || !this.modalTitle ? null : this.titleId;
        }
    },
    mounted() {
        this.keydownHandler = (event) => {
            if (event.key === "Escape" && (!this.keyboardDismiss || this.isBusy)) {
                event.stopImmediatePropagation();
                event.preventDefault();
            }
        };
        this.hideHandler = (event) => {
            if (this.suppressNextHidden || this.approvedDismissal) {
                this.approvedDismissal = false;
                this.beginHide();
                return;
            }
            if (this.isBusy || this.dismissalPending) {
                event.preventDefault();
                return;
            }
            const result = this.beforeClose?.();
            if (!result?.then) {
                if (result === false) event.preventDefault();
                else {
                    this.beginHide();
                }
                return;
            }
            event.preventDefault();
            this.dismissalPending = true;
            Promise.resolve(result).then((allowed) => {
                if (allowed !== false && !this.isBusy && this.$refs.modalEl) {
                    this.approvedDismissal = true;
                    this.requestClose();
                }
            }).catch(() => false).finally(() => {
                this.dismissalPending = false;
            });
        };
        this.showHandler = (event) => {
            this.phase = "opening";
            this.returnFocusTarget = dialogTrigger(event.relatedTarget || document.activeElement);
            registerModal(this.$refs.modalEl);
        };
        this.shownHandler = () => {
            this.phase = "shown";
            this.isShown = true;
            modalShown(this.$refs.modalEl);
            if (this.destroying || this.closeWhenShown) {
                this.closeWhenShown = false;
                this.close();
                return;
            }
            if (this.userCloseWhenShown) {
                this.userCloseWhenShown = false;
                this.requestClose();
                return;
            }
            if (!this.$refs.modalEl.contains(document.activeElement)) {
                this.$refs.modalEl.focus({ preventScroll: true });
            }
            this.$emit("shown");
        };
        this.hiddenHandler = () => {
            this.phase = "hidden";
            this.isShown = false;
            releaseModal(this.$refs.modalEl);
            const target = this.returnFocusTarget;
            const focus = document.activeElement;
            // Preserve deliberate handoffs to another dialog or a page field.
            if (
                target?.isConnected &&
                target.getClientRects().length &&
                (!topModal() || topModal().contains(target)) &&
                (focus === document.body || this.$refs.modalEl.contains(focus))
            ) {
                target.focus({ preventScroll: true });
            }
            this.returnFocusTarget = null;
            // Programmatic close() sets the suppress flag so this listener
            // only emits `close` for user-driven dismissals (X, Esc, backdrop).
            if (this.suppressNextHidden || this.destroying) {
                this.suppressNextHidden = false;
            } else {
                this.$emit("close");
            }
            this.resolveClose?.();
            this.resolveClose = null;
            this.closePromise = null;
        };
        this.$refs.modalEl.addEventListener("show.bs.modal", this.showHandler);
        this.$refs.modalEl.addEventListener("shown.bs.modal", this.shownHandler);
        this.$refs.modalEl.addEventListener("hidden.bs.modal", this.hiddenHandler);
        this.$refs.modalEl.addEventListener("hide.bs.modal", this.hideHandler);
        this.$refs.modalEl.addEventListener("keydown", this.keydownHandler, true);
        // Eager registration also covers data-bs-toggle launchers.
        this.openedModal = new Modal(this.$refs.modalEl, { keyboard: true, focus: false });
    },
    beforeUnmount() {
        this.destroying = true;
        const element = this.$refs.modalEl;
        const backdrop = modalBackdrop(element);
        // Finish queued Bootstrap callbacks before disposing; dispose alone does
        // not release body styles and a pending show can append the removed node.
        element?.classList.remove("fade");
        backdrop?.classList.remove("fade");
        backdrop?.dispatchEvent(new Event("transitionend"));
        element?.querySelector(".modal-dialog")?.dispatchEvent(new Event("transitionend"));
        if (this.phase === "shown") this.close();
        element?.dispatchEvent(new Event("transitionend"));
        backdrop?.dispatchEvent(new Event("transitionend"));
        releaseModal(element);
        this.openedModal?.dispose();
        this.resolveClose?.();
        this.$refs.modalEl?.removeEventListener("hide.bs.modal", this.hideHandler);
        this.$refs.modalEl?.removeEventListener("keydown", this.keydownHandler, true);
        if (this.$refs.modalEl && this.showHandler) {
            this.$refs.modalEl.removeEventListener("show.bs.modal", this.showHandler);
        }
        if (this.$refs.modalEl && this.shownHandler) {
            this.$refs.modalEl.removeEventListener("shown.bs.modal", this.shownHandler);
        }
        if (this.$refs.modalEl && this.hiddenHandler) {
            this.$refs.modalEl.removeEventListener("hidden.bs.modal", this.hiddenHandler);
        }
    },
    methods: {
        beginHide() {
            this.phase = "closing";
            this.isShown = false;
            if (!this.closePromise) {
                this.closePromise = new Promise((resolve) => { this.resolveClose = resolve; });
            }
        },
        requestClose() {
            if (this.phase === "opening") {
                this.userCloseWhenShown = true;
                return;
            }
            const element = this.$refs.modalEl;
            (this.openedModal ?? Modal.getInstance(element))?.hide();
        },
        close() {
            const modalElement = this.$refs.modalEl;
            if (!modalElement) return Promise.resolve();
            const modal = this.openedModal ?? Modal.getInstance(modalElement);
            if (!modal || this.phase === "hidden") return Promise.resolve();
            if (this.phase === "closing") return this.closePromise || Promise.resolve();
            if (!this.closePromise) {
                this.closePromise = new Promise((resolve) => { this.resolveClose = resolve; });
            }
            const hidden = this.closePromise;

            // Bootstrap ignores hide() while the opening transition is still
            // running. Queue the close until shown.bs.modal so fast form
            // interactions cannot leave a completed dialog on screen.
            if (this.phase === "opening") {
                this.closeWhenShown = true;
                return hidden;
            }
            this.suppressNextHidden = true;
            // Clear Vue's mirrored state before Bootstrap starts its hide
            // transition. A parent update during the transition must not
            // render the `show` class back onto the modal.
            this.isShown = false;
            this.phase = "closing";
            modal.hide();
            return hidden;
        },
        open() {
            const modalElement = this.$refs.modalEl;
            if (!modalElement || modalElement.classList.contains("show")) return;
            if (this.openedModal) {
                this.openedModal.show();
                return;
            }
            const existing = Modal.getInstance(modalElement);
            if (existing) {
                existing.show();
            } else {
                // The capture listener reads the current prop on every Escape.
                // Bootstrap's instance configuration is otherwise fixed at creation.
                this.openedModal = new Modal(modalElement, { keyboard: true, focus: false });
                this.openedModal.show();
            }
        }
    }
};
</script>

<style>
.modal-fullscreen .modal-header {
    padding-top: max(var(--forkto-safe-area-top), 1rem);
}

.modal-mobile .modal-fullscreen .modal-content {
    border-radius: 0 !important;
}

.modal-fullscreen .modal-header,
.modal-fullscreen .modal-footer {
    padding-left: max(1rem, var(--forkto-safe-area-left, 0px));
    padding-right: max(1rem, var(--forkto-safe-area-right, 0px));
}

.modal-fullscreen .modal-body {
    -webkit-overflow-scrolling: touch;
    padding-left: max(0.5rem, var(--forkto-safe-area-left, 0px));
    padding-right: max(0.5rem, var(--forkto-safe-area-right, 0px));
}

/* Mobile fullscreen modals use the standard Bootstrap gutter unless a
   data-dense workflow explicitly opts into compact spacing. These classes are
   only attached on mobile, preserving existing desktop fullscreen layouts. */
.modal-fullscreen.modal-mobile-body-normal .modal-body {
    padding-left: max(1rem, var(--forkto-safe-area-left, 0px));
    padding-right: max(1rem, var(--forkto-safe-area-right, 0px));
}

.modal-fullscreen.modal-mobile-body-compact .modal-body {
    padding-left: max(0.5rem, var(--forkto-safe-area-left, 0px));
    padding-right: max(0.5rem, var(--forkto-safe-area-right, 0px));
}

.modal-fullscreen.modal-mobile-header-hidden .modal-body {
    padding-top: max(var(--forkto-safe-area-top), 1rem);
}

.modal-fullscreen.modal-mobile-body-safe-bottom .modal-body {
    padding-bottom: max(var(--forkto-safe-area-bottom), 1rem);
}

.modal-fullscreen .modal-content:not(:has(.modal-footer)) .modal-body {
    padding-bottom: max(var(--forkto-safe-area-bottom, 0px), 1rem);
}

/* Slotted form components often wrap their modal body and footer in a single
   <form>. Bootstrap's scrollable-dialog rules expect those elements to be
   direct children of .modal-content, so make the form wrapper participate in
   both the scrollable desktop and fullscreen mobile flex layouts. */
.modal-dialog-scrollable .modal-content > form,
.modal-fullscreen .modal-content > form {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
}

.modal-fullscreen .modal-footer,
.modal.bottom-sheet .modal-footer {
    padding-bottom: max(var(--forkto-safe-area-bottom), 1rem) !important;
}

.modal.bottom-sheet .modal-body,
.modal.bottom-sheet .modal-footer {
    text-align: left;
}

.modal-mobile .modal-header,
.modal-mobile .modal-body,
.modal-mobile .modal-footer {
    padding-left: max(var(--bs-modal-padding, 1rem), var(--forkto-safe-area-left, 0px));
    padding-right: max(var(--bs-modal-padding, 1rem), var(--forkto-safe-area-right, 0px));
}

/* L13: the translucent blurred backdrop applies to bottom-sheet modals on
   desktop only (not all desktop modals) — the narrow scope is intentional. */
.modal.bottom-sheet:not(.modal-mobile) {
    background-color: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(2px);
}
.modal-mobile .modal-header .btn-close {
    box-sizing: border-box;
    width: 44px;
    min-width: 44px;
    height: 44px;
    min-height: 44px;
    padding: 0;
    background-size: 1rem;
}

.modal-mobile .modal-footer .btn {
    min-height: 44px;
}

.modal-mobile.bottom-sheet .modal-dialog {
    margin: 0;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    max-width: 100%;
    min-height: 0;
    align-items: flex-end;
}
.modal-mobile.bottom-sheet .modal-content {
    border-radius: var(--forkto-radius-surface) var(--forkto-radius-surface) 0 0;
}
.modal-mobile.bottom-sheet.fade .modal-dialog {
    transform: translateY(100%);
    transition: transform 0.3s ease-out;
}
.modal-mobile.bottom-sheet.show .modal-dialog {
    transform: translateY(0);
}
</style>
